// Google Sheets Sync - Uploads to YOUR specific sheet every 60 seconds
// Sheet: https://docs.google.com/spreadsheets/d/1RlxULWcjsSY3K8L-Mndn-f6bEeP2ScsAP6kunYfYOrA/edit

class GoogleSheetsSync {
  // YOUR GOOGLE SHEET - Will be set up with Apps Script
  private readonly SHEET_URL =
    'https://script.google.com/macros/s/YOUR_DEPLOYMENT_ID/exec'
  // ☝️ Replace with your Apps Script deployment URL (see instructions below)

  private syncInterval: NodeJS.Timeout | null = null
  private isSyncing = false
  private lastSyncTime = 0

  startAutoSync() {
    if (this.syncInterval) return

    console.log('[GoogleSheets] 🔄 Auto-sync enabled (every 60 seconds)')

    this.syncInterval = setInterval(() => {
      this.syncAllData()
    }, 60000) // 60 seconds

    // First sync after 10 seconds
    setTimeout(() => this.syncAllData(), 10000)
  }

  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval)
      this.syncInterval = null
    }
  }

  async syncAllData() {
    if (this.isSyncing) {
      console.log('[GoogleSheets] ⏳ Sync already in progress, skipping')
      return
    }

    // Prevent syncing more than once per minute
    const now = Date.now()
    if (now - this.lastSyncTime < 55000) {
      return
    }

    this.isSyncing = true
    this.lastSyncTime = now

    try {
      const data = this.collectAllData()

      // Only sync if there's data
      if (
        !data.person &&
        data.sessions.length === 0 &&
        data.chats.length === 0
      ) {
        console.log('[GoogleSheets] ℹ️ No data to sync yet')
        this.isSyncing = false
        return
      }

      console.log('[GoogleSheets] 📤 Syncing data:', {
        person: data.person?.name || 'Anonymous',
        sessions: data.sessions.length,
        chats: data.chats.length,
        events: data.events.length,
      })

      await this.sendToSheets(data)

      console.log('[GoogleSheets] ✅ Sync successful')
    } catch (error) {
      console.error('[GoogleSheets] ❌ Sync failed:', error)
    } finally {
      this.isSyncing = false
    }
  }

  private collectAllData() {
    const safeGet = (key: string): any => {
      try {
        const data = localStorage.getItem(key)
        if (!data) return null
        return JSON.parse(data)
      } catch (error) {
        return null
      }
    }

    const safeGetArray = (key: string): any[] => {
      try {
        const data = localStorage.getItem(key)
        if (!data) return []
        const parsed = JSON.parse(data)
        return Array.isArray(parsed) ? parsed : []
      } catch (error) {
        return []
      }
    }

    const person = safeGet('person_profile')
    const sessions = safeGetArray('sessions')
    const chats = safeGetArray('chats')
    const events = safeGetArray('events')

    return {
      person,
      sessions,
      chats,
      events,
      timestamp: new Date().toISOString(),
    }
  }

  private async sendToSheets(data: any) {
    try {
      const response = await fetch(this.SHEET_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        mode: 'no-cors', // Required for Google Apps Script
      })

      // Note: no-cors means we can't read response, but data is sent
      return true
    } catch (error) {
      console.error('[GoogleSheets] Send error:', error)
      throw error
    }
  }

  async manualSync() {
    console.log('[GoogleSheets] 🔄 Manual sync triggered')
    await this.syncAllData()
  }
}

export const googleSheetsSync = new GoogleSheetsSync()

// Auto-start on page load
if (typeof window !== 'undefined') {
  if (document.readyState === 'complete') {
    googleSheetsSync.startAutoSync()
  } else {
    window.addEventListener('load', () => {
      googleSheetsSync.startAutoSync()
    })
  }

  // Final sync before page close
  window.addEventListener('beforeunload', () => {
    googleSheetsSync.syncAllData()
  })
}
